import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { interpolatePosition } from './replayUtils';

/** Available speed presets for playback */
export const SPEED_PRESETS = [0.5, 1, 2, 5, 10, 20];

/**
 * Custom hook for route replay playback engine.
 * Manages play/pause, progress (0-1), speed multiplier, and animated marker position.
 *
 * @param {Array|null} positions - Array of Traccar position objects (from api.getRoute)
 * @returns {{ progress, playing, speed, setSpeed, toggle, seek, reset, currentIndex, currentPosition }}
 */
export function useReplayEngine(positions) {
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(null);

  // Total trip duration in ms (from first to last position timestamp)
  const totalDuration = useMemo(() => {
    if (!positions || positions.length < 2) return 0;
    return (
      new Date(positions[positions.length - 1].fixTime).getTime() -
      new Date(positions[0].fixTime).getTime()
    );
  }, [positions]);

  // Animation loop
  useEffect(() => {
    if (!playing || !totalDuration) return;

    const animate = (timestamp) => {
      if (!lastTimeRef.current) {
        lastTimeRef.current = timestamp;
      }
      const delta = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      setProgress((prev) => {
        const next = prev + (delta * speed) / totalDuration;
        if (next >= 1) {
          setPlaying(false);
          return 1;
        }
        return next;
      });

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafRef.current);
      lastTimeRef.current = null; // Reset to avoid time jumps on resume
    };
  }, [playing, speed, totalDuration]);

  // Reset progress when positions change
  useEffect(() => {
    setProgress(0);
    setPlaying(false);
  }, [positions]);

  const toggle = useCallback(() => {
    setPlaying((p) => {
      if (!p) {
        // If at end, restart
        setProgress((prev) => (prev >= 1 ? 0 : prev));
      }
      return !p;
    });
  }, []);

  const seek = useCallback((p) => {
    setProgress(Math.max(0, Math.min(1, p)));
  }, []);

  const reset = useCallback(() => {
    setProgress(0);
    setPlaying(false);
  }, []);

  // Derive current index and interpolated position
  const currentIndex = useMemo(() => {
    if (!positions || positions.length === 0) return 0;
    return Math.min(
      Math.floor(progress * (positions.length - 1)),
      positions.length - 1
    );
  }, [progress, positions]);

  const currentPosition = useMemo(() => {
    if (!positions || positions.length === 0) return null;
    return interpolatePosition(positions, progress);
  }, [positions, progress]);

  return {
    progress,
    playing,
    speed,
    setSpeed,
    toggle,
    seek,
    reset,
    currentIndex,
    currentPosition,
  };
}
